// Main Icons
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import GamesOutlinedIcon from "@mui/icons-material/GamesOutlined";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import GroupIcon from "@mui/icons-material/Group";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PieChartIcon from "@mui/icons-material/PieChart";
import SettingsIcon from "@mui/icons-material/Settings";

type MenuItem = {
  id: number;
  icon: JSX.Element;
  label: string;
  link: string;
  allowedRoles: string[];
  children?: MenuItem[];
};

const getIconColor = (menuLabel: string): "primary" | "secondary" => {
  return menuLabel === localStorage.getItem("HighlightsMenu")
    ? "primary"
    : "secondary";
};

const buildMenu = (): MenuItem[] => [
  {
    id: 0,
    icon: <AddBoxOutlinedIcon color={getIconColor("Location Details")} />,
    label: "Location Details",
    link: "Location-Details",
    allowedRoles: ["Super Admin", "Admin", "Standard User", "Viewers"],
  },
  {
    id: 1,
    icon: <GamesOutlinedIcon color={getIconColor("GHG Targets")} />,
    label: "GHG Targets",
    link: "Target-Form",
    allowedRoles: ["Super Admin", "Admin", "Standard User", "Viewers"],
  },
  {
    id: 2,
    icon: <ViewInArOutlinedIcon color={getIconColor("Reduction Measures")} />,
    label: "Reduction Measures",
    link: "Reduction-Measures",
    allowedRoles: ["Super Admin", "Admin", "Standard User", "Viewers"],
  },
  {
    id: 3,
    icon: <SettingsIcon color={getIconColor("Configuration")} />,
    label: "Configuration",
    link: "prices",
    allowedRoles: ["Super Admin", "Admin", "Standard User", "Viewers"],
    children: [
      {
        id: 1,
        icon: <PieChartIcon color={getIconColor("Configuration")} />,
        label: "Prices",
        link: "prices",
        allowedRoles: ["Super Admin", "Admin", "Standard User", "Viewers"],
      },
      {
        id: 2,
        icon: <PieChartIcon color={getIconColor("UserRole")} />,
        label: "Discount Factor",
        link: "discountfactor",
        allowedRoles: ["Super Admin"],
      },
    ],
  },
  {
    id: 4,
    icon: <DashboardIcon color={getIconColor("Dashboards")} />,
    label: "Dashboards",
    link: "baselinetargetemissions",
    allowedRoles: ["Super Admin", "Admin", "Standard User", "Viewers"],
    children: [
      {
        id: 1,
        icon: <PieChartIcon color={getIconColor("Dashboards")} />,
        label: "Baseline Emissions",
        link: "baselinetargetemissions",
        allowedRoles: ["Super Admin", "Admin", "Standard User", "Viewers"],
      },
      {
        id: 2,
        icon: <PieChartIcon color={getIconColor("Dashboards")} />,
        label: "Project Register",
        link: "projectregister",
        allowedRoles: ["Super Admin", "Admin", "Standard User", "Viewers"],
      },
      {
        id: 3,
        icon: <PieChartIcon color={getIconColor("Dashboards")} />,
        label: "Decarbonization Plans",
        link: "scenarioView",
        allowedRoles: ["Super Admin", "Admin", "Standard User", "Viewers"],
      },
    ],
  },
  {
    id: 5,
    icon: <GroupIcon color={getIconColor("Settings")}  />,
    label: "Settings",
    link: "user-management",
    allowedRoles: ["Super Admin", "Admin"],
    children: [
      {
        id: 1,
        icon: <GroupIcon color={getIconColor("User Plan")} />,
        label: "User Plan",
        link: "user-management",
        allowedRoles: ["Super Admin", "Admin"],
      },
      {
        id: 2,
        icon: <PieChartIcon color={getIconColor("User Role Configuration")} />,
        label: "User Role Configuration",
        link: "userrole",
        allowedRoles: ["Super Admin"],
      },
      {
        id: 3,
        icon: <PieChartIcon color={getIconColor("Location Image")} />,
        label: "Location Image",
        link: "configuration",
        allowedRoles: ["Super Admin", "Admin"],
      },
    ],
  },
];

const filterMenusByRole = (menus: MenuItem[], userRole?: string): MenuItem[] => {
  if (!userRole) {
    return menus;
  }

  return menus.reduce<MenuItem[]>((filteredMenus, menu) => {
    if (!menu.allowedRoles.includes(userRole)) {
      return filteredMenus;
    }

    const filteredMenu = menu.children
      ? {
          ...menu,
          children: menu.children.filter((child) =>
            child.allowedRoles.includes(userRole)
          ),
        }
      : menu;

    filteredMenus.push(filteredMenu);
    return filteredMenus;
  }, []);
};

const SimpleMenu = (userRole?: string) => {
  const isPortfolioview = localStorage.getItem("Portfolioview");
  const allMenus = filterMenusByRole(buildMenu(), userRole);

  // If "Portfolio view" was clicked, show only that menu item
  if (isPortfolioview === "1") {
    return allMenus.filter((menu) => menu.label === "Portfolio view");
  }

  // Otherwise, return all menu items
  return allMenus;
};

export default SimpleMenu;
